import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useSEO } from "@/lib/seo";
import { Mail, MapPin, Phone, Clock, CheckCircle2 } from "lucide-react";

// TODO: replace these placeholders with real details when provided.
const CONTACT = {
  email: "contact@paysignal.ai",
  phone: "(855) 961-7425",
  address: "Retention Logix LLC, 151 Calle San Francisco, San Juan, PR 00901",
  hours: "Monday–Friday, 9:00am–5:00pm",
  response: "within one business day",
};

const TOPICS = ["Account question", "Billing", "Text messages & opt-out", "Privacy request", "Something else"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [sent, setSent] = useState(false);

  useSEO({
    title: "Contact PaySignal — Support for freelancers & gig workers",
    description: "Get in touch with the PaySignal team. Questions about your account, billing, text alerts, or privacy — we reply within one business day.",
    canonical: window.location.origin + "/contact",
  });

  const submit = (e) => {
    e.preventDefault();
    // Demo form — routes to CONTACT.email in production.
    setSent(true);
  };

  const Detail = ({ Icon, label, value }) => (
    <div className="flex items-start gap-3">
      <span className="grid place-items-center w-9 h-9 rounded-lg bg-brandblue-light text-brandblue shrink-0"><Icon size={16} /></span>
      <div><p className="text-xs text-ink-muted">{label}</p><p className="text-sm font-medium text-midnight">{value}</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream text-espresso">
      <SiteHeader />

      <section className="max-w-3xl mx-auto px-6 pt-16 pb-8">
        <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-brandblue bg-brandblue-light rounded-full px-3 py-1.5">
          <span className="w-2 h-2 rounded-full bg-lime" /> Contact
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-5 text-midnight leading-[1.05]" data-testid="contact-title">Get in touch.</h1>
        <p className="text-lg text-ink-secondary mt-4">Support is answered by a real person during business hours. We reply to email {CONTACT.response}.</p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20 grid md:grid-cols-5 gap-8">
        {/* Details */}
        <div className="md:col-span-2 space-y-5">
          <div className="border rounded-2xl bg-white p-6 space-y-5" data-testid="contact-details">
            <Detail Icon={Mail} label="Email" value={CONTACT.email} />
            <Detail Icon={Phone} label="Phone" value={<a href="tel:+18559617425" className="hover:text-brandblue transition-colors" data-testid="contact-phone-link">{CONTACT.phone}</a>} />
            <Detail Icon={MapPin} label="Mailing address" value={CONTACT.address} />
            <Detail Icon={Clock} label="Support hours" value={CONTACT.hours} />
          </div>
          <div className="border rounded-2xl bg-lavender/40 p-5">
            <p className="text-sm text-midnight leading-relaxed">
              To stop text alerts, reply <span className="font-semibold">STOP</span> to any message, or reply <span className="font-semibold">HELP</span> for help. See our{" "}
              <Link to="/sms-terms" className="text-brandblue underline">SMS Terms</Link>.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="md:col-span-3">
          <div className="border rounded-2xl bg-white p-7">
            {sent ? (
              <div className="py-10 text-center" data-testid="contact-success">
                <span className="inline-grid place-items-center w-14 h-14 rounded-full bg-[#E7F6EF] text-status-success mx-auto animate-pop-in"><CheckCircle2 size={30} /></span>
                <h2 className="font-heading text-xl font-bold text-midnight mt-4">Message sent</h2>
                <p className="text-sm text-ink-secondary mt-1">Thanks, {form.name || "there"} — we reply {CONTACT.response} during {CONTACT.hours}.</p>
                <Button variant="outline" className="mt-6" onClick={() => { setSent(false); setForm({ name: "", email: "", topic: "", message: "" }); }} data-testid="contact-reset">Send another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <h2 className="font-heading text-lg font-bold text-midnight">Send us a message</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Topic</Label>
                  <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                    <SelectTrigger data-testid="contact-topic"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
                    <SelectContent>{TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="message">Message</Label>
                  <Textarea id="message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
                </div>
                <Button type="submit" className="w-full" data-testid="contact-submit">Send message</Button>
                <p className="text-xs text-ink-muted text-center">This is a demo form. In production it routes to {CONTACT.email}.</p>
              </form>
            )}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
